import React, { useEffect, useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import PageShell, { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Modal from '../../ui/Modal';
import { FormGrid, Field, Input, Select } from '../../ui/Form';
import { useAuth } from '../auth';
import HelpButton from '../help/HelpButton';

const PAYMENT_TYPES = ['قسط', 'أعفاء من القسط'];
const METHODS = [{ value: 'cash', label: 'نقداً' }, { value: 'transfer', label: 'حوالة' }, { value: 'other', label: 'أخرى' }];

const money = (cents) => `$${(cents || 0).toLocaleString('en-US')}`;

const MONTH_NAMES = ['كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران', 'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'];
const monthLabel = (yyyyMm) => {
  if (!yyyyMm) return '—';
  const [y, m] = yyyyMm.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};
// How many whole months an amount added to the existing credit would
// cover, and the month it would reach — mirrors the server's own
// forward-fill math in money.js so the manager sees the effect before
// confirming, not just after.
const addMonths = (yyyyMm, n) => {
  const [y, m] = yyyyMm.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
};

export default function Payments() {
  const toast = useToastWithSound();
  const { user } = useAuth();
  const { data, loading, error, reload } = useData(async () => {
    const [payments, students, overdue] = await Promise.all([api.payments(), api.students(), api.overdueStudents()]);
    return { payments, students, overdue };
  }, []);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ student_id: '', amount_cents: '', payment_type: 'قسط', payment_method: 'cash', note: '' });
  const [saving, setSaving] = useState(false);
  const dirty = useDirtyForm(form);

  // Re-fetched every time the picked student changes, so the modal can
  // show "paid through" / overdue and preview what the entered amount
  // will cover before the manager confirms.
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!form.student_id) { setSummary(null); return; }
    let cancelled = false;
    api.studentPaymentSummary(form.student_id).then((s) => { if (!cancelled) setSummary(s); }).catch(() => {});
    return () => { cancelled = true; };
  }, [form.student_id]);

  const students = data?.students || [];
  // The payer's name and section come on the payment row itself.
  const rows = (data?.payments || []).map((p) => ({
    ...p,
    student_name: p.student_name || `#${p.student_id}`,
    section_label: p.section_label || '—',
  }));

  const overdue = data?.overdue || [];

  // Opens the payment modal pre-filled to a specific student — used by
  // the "تسجيل دفعة" action on the overdue list, so the manager doesn't
  // have to re-pick the student from the dropdown.
  const openNew = (studentId) => {
    const id = typeof studentId === 'number' ? studentId : '';
    const next = { student_id: id ? String(id) : '', amount_cents: '', payment_type: 'قسط', payment_method: 'cash', note: '' };
    setForm(next); dirty.reset(next); setCreating(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.student_id) return toast.error('اختر الطالب');
    const isExempt = form.payment_type === 'أعفاء من القسط';
    if (!isExempt && (!form.amount_cents || Number(form.amount_cents) <= 0)) return toast.error('المبلغ مطلوب');
    setSaving(true);
    try {
      await api.createPayment({
        student_id: Number(form.student_id),
        amount_cents: isExempt ? 0 : Number(form.amount_cents),
        payment_type: form.payment_type,
        payment_method: isExempt ? undefined : form.payment_method,
        note: form.note || undefined,
        paid_by: user.id,
      });
      toast.success('تم تسجيل الدفعة');
      setCreating(false);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell title="الأقساط" description="سجل الدفعات المالية"
      actions={(
        <>
          <HelpButton guides={['payment-add']} />
          <Button onClick={() => openNew()}>+ تسجيل دفعة</Button>
        </>
      )}>
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      {overdue.length > 0 && (
        <Table
          keyField="student_id"
          rows={overdue}
          emptyText="لا يوجد طلاب متأخرون"
          searchPlaceholder="ابحث باسم الطالب..."
          columns={[
            { key: 'student_name', label: 'الطالب متأخر عن الشهر الحالي', width: 2, filterable: false, render: (r) => <strong>{r.student_name}</strong> },
            { key: 'monthly_due_cents', label: 'القسط الشهري', nowrap: true, align: 'end', filterable: false, formatValue: (v) => money(v) },
            { key: 'remaining_cents', label: 'المتأخر', nowrap: true, align: 'end', filterable: false, formatValue: (v) => money(v) },
            { key: 'paid_through_month', label: 'مدفوع حتى', nowrap: true, align: 'center', filterable: false, formatValue: (v) => monthLabel(v) },
          ]}
          renderActions={(r) => (
            <button type="button" className="ui-btn-mini ui-btn-mini-primary" onClick={() => openNew(r.student_id)}>
              تسجيل دفعة
            </button>
          )}
        />
      )}

      <Table
        keyField="id"
        rows={rows}
        loading={loading}
        emptyText="لا توجد دفعات مسجّلة"
        searchPlaceholder="ابحث باسم الطالب..."
        columns={[
          { key: 'student_name', label: 'الطالب', width: 2, filterable: false, render: (r) => <strong>{r.student_name}</strong> },
          { key: 'payment_type', label: 'النوع', nowrap: true, align: 'center',
            render: (r) => <span className={`ui-badge ${r.payment_type === 'أعفاء من القسط' ? 'ui-badge-info' : 'ui-badge-success'}`}>{r.payment_type}</span> },
          { key: 'amount_cents', label: 'المبلغ', nowrap: true, align: 'end', filterable: false, formatValue: (v) => money(v) },
          { key: 'remaining_after_cents', label: 'المتبقي بعدها', nowrap: true, align: 'end', filterable: false, formatValue: (v) => money(v) },
          { key: 'payment_method', label: 'طريقة الدفع', nowrap: true, align: 'center',
            formatValue: (v) => METHODS.find((m) => m.value === v)?.label || '—' },
          { key: 'paid_at', label: 'التاريخ', nowrap: true, filterable: false, formatValue: (v) => (v || '').slice(0, 10) },
        ]}
      />

      {creating && (
        <Modal open size="md" title="تسجيل دفعة" onClose={() => setCreating(false)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving}>حفظ</Button>
            </>
          )}>
          <form onSubmit={save}>
            <FormGrid columns={2}>
              <Field label="الطالب" required span>
                <Select value={form.student_id} onChange={set('student_id')} placeholder="اختر الطالب"
                  options={students.filter((s) => s.status === 'active').map((s) => ({ value: s.id, label: s.name }))} />
              </Field>

              {summary && (
                <div className="ui-field-span" style={{ gridColumn: '1 / -1' }}>
                  <div className={`ui-notice ${summary.current_month_paid ? 'ui-notice-info' : 'ui-notice-warning'}`}>
                    <div className="ui-notice-body">
                      القسط الشهري: <strong>{money(summary.monthly_due_cents)}</strong>
                      {' — '}مدفوع حتى: <strong>{monthLabel(summary.paid_through_month)}</strong>
                      {' — '}
                      {summary.status === 'أعفاء من القسط'
                        ? <strong>معفى من القسط</strong>
                        : summary.current_month_paid
                          ? <strong>الشهر الحالي مسدد</strong>
                          : <strong>متأخر — الشهر الحالي غير مسدد</strong>}
                      {summary.credit_cents > 0 && <> — رصيد جزئي: {money(summary.credit_cents)}</>}
                      {form.payment_type !== 'أعفاء من القسط' && Number(form.amount_cents) > 0 && summary.monthly_due_cents > 0 && (() => {
                        // Same forward-fill as the server: anchor is the
                        // last paid month, or one month before the
                        // billing start if nothing's been paid yet.
                        const anchor = summary.paid_through_month || addMonths(summary.current_month, -1);
                        const monthsGained = Math.floor((summary.credit_cents + Number(form.amount_cents)) / summary.monthly_due_cents);
                        return monthsGained > 0 ? (
                          <> — هذا المبلغ سيغطي حتى: <strong>{monthLabel(addMonths(anchor, monthsGained))}</strong></>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              )}

              <Field label="النوع" required>
                <Select value={form.payment_type} onChange={set('payment_type')} options={PAYMENT_TYPES} />
              </Field>
              {form.payment_type !== 'أعفاء من القسط' && (
                <>
                  <Field label="المبلغ" required><Input type="number" value={form.amount_cents} onChange={set('amount_cents')} /></Field>
                  <Field label="طريقة الدفع"><Select value={form.payment_method} onChange={set('payment_method')} options={METHODS} /></Field>
                </>
              )}
              <Field label="ملاحظة" span><Input value={form.note} onChange={set('note')} /></Field>
            </FormGrid>
          </form>
        </Modal>
      )}
    </PageShell>
  );
}
